/*
  New Arrivals date picker.

  Filters the collection grid to a single release day by the custom.publish_date storefront filter
  (filter.p.m.custom.publish_date). Rendering goes through FacetFiltersForm.renderPage (facets.js), which
  fetches the section via the Section Rendering API, swaps the grid and updates the URL/history.

  The metafield is free text in mixed formats ("02 July 2026" and "2 July 2026"), so release days and their
  exact stored values are loaded from the arrivals-dates section and sent verbatim as filter values.
*/
if (!customElements.get('arrivals-date-picker')) {
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const pad = (number) => String(number).padStart(2, '0');
  const toIso = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

  // "02 July 2026" / "2 July 2026" -> "2026-07-02"
  const rawToIso = (raw) => {
    const match = /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/.exec(raw.trim());
    if (!match) return null;
    const month = MONTHS.findIndex((name) => name.toLowerCase() === match[2].toLowerCase());
    return month === -1 ? null : `${match[3]}-${pad(month + 1)}-${pad(match[1])}`;
  };

  class ArrivalsDatePicker extends HTMLElement {
    // Shared by the desktop and mobile instances so the dates are only fetched once
    static releaseDates = null;

    static loadReleaseDates(url) {
      if (ArrivalsDatePicker.releaseDates) return ArrivalsDatePicker.releaseDates;

      const fetchPage = (page) =>
        fetch(`${url}&page=${page}`)
          .then((response) => {
            if (!response.ok) throw new Error(`arrivals-dates responded ${response.status}`);
            return response.text();
          })
          .then((html) => {
            const data = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-arrival-dates]');
            return { pages: Number(data.dataset.pages) || 1, entries: JSON.parse(data.textContent) };
          });

      ArrivalsDatePicker.releaseDates = fetchPage(1)
        .then((first) => {
          const rest = Array.from({ length: first.pages - 1 }, (_, index) => fetchPage(index + 2));
          return Promise.all(rest).then((pages) => [first, ...pages].flatMap((page) => page.entries));
        })
        .then((entries) => {
          // ISO date -> every raw value stored for that day
          const byIso = new Map();
          entries.forEach(({ raw, iso }) => {
            if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return;
            if (!byIso.has(iso)) byIso.set(iso, new Set());
            byIso.get(iso).add(raw);
          });
          return byIso;
        });

      return ArrivalsDatePicker.releaseDates;
    }

    connectedCallback() {
      this.param = this.dataset.param;
      this.details = this.querySelector('details');
      this.summary = this.details.querySelector('summary');
      this.removeLink = this.querySelector('[data-remove-date]');
      this.inputsContainer = this.querySelector('[data-date-inputs]');
      this.label = this.summary.getAttribute('aria-label');
      this.datesByIso = new Map();

      if (typeof flatpickr === 'undefined') return;

      this.picker = flatpickr(this.querySelector('.flatpickr'), {
        wrap: true,
        altInput: true,
        altFormat: 'j F Y',
        dateFormat: 'Y-m-d',
        disableMobile: true,
        // Nothing is selectable until the release days have loaded
        enable: [() => false],
        // Clicking the summary toggles the calendar itself, so it mustn't count as an outside click
        ignoredFocusElements: [this.summary],
        onChange: (selectedDates) => {
          if (selectedDates.length) this.selectDate(selectedDates[0]);
        },
        onClose: () => {
          if (this.details.open) this.details.open = false;
        },
      });

      this.details.addEventListener('toggle', () => {
        if (this.details.open && !this.picker.isOpen) this.picker.open();
        if (!this.details.open && this.picker.isOpen) this.picker.close();
      });

      // flatpickr fires input/change on its inputs; keep them from triggering the facet form's own submit
      ['input', 'change'].forEach((type) => this.addEventListener(type, (event) => event.stopPropagation()));

      this.removeLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.render([]);
      });

      this.onFacetsRender = (event) => this.sync(event.detail.searchParams);
      document.addEventListener('facets:render', this.onFacetsRender);

      this.sync(window.location.search);

      ArrivalsDatePicker.loadReleaseDates(this.dataset.datesUrl)
        .then((datesByIso) => {
          this.datesByIso = datesByIso;
          const releaseDays = [...datesByIso.keys()].sort();
          this.picker.set({
            enable: releaseDays,
            minDate: releaseDays[0],
            maxDate: releaseDays[releaseDays.length - 1],
          });
          this.sync(window.location.search);
        })
        .catch((error) => {
          // Fall back to an unrestricted calendar rather than a dead one
          console.error(error);
          this.picker.set('enable', []);
          this.sync(window.location.search);
        });
    }

    disconnectedCallback() {
      document.removeEventListener('facets:render', this.onFacetsRender);
      if (this.picker) this.picker.destroy();
    }

    selectDate(date) {
      const iso = toIso(date);
      let values = [...(this.datesByIso.get(iso) || [])];

      // Dates not loaded yet: cover both stored formats
      if (!values.length) {
        const suffix = `${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
        values = [...new Set([`${pad(date.getDate())} ${suffix}`, `${date.getDate()} ${suffix}`])];
      }

      this.render(values);
    }

    render(values) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete(this.param);
      searchParams.delete('page');
      values.forEach((value) => searchParams.append(this.param, value));

      FacetFiltersForm.renderPage(searchParams.toString());
    }

    // Reflect the date in the URL: calendar selection, "Remove date" link and hidden form inputs
    sync(search) {
      const values = new URLSearchParams(search).getAll(this.param);
      const iso = values.map(rawToIso).find(Boolean);

      this.inputsContainer.replaceChildren(
        ...values.map((value) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = this.param;
          input.value = value;
          return input;
        })
      );

      this.removeLink.classList.toggle('hidden', values.length === 0);
      this.summary.setAttribute('aria-label', values.length ? `${this.label}: ${values[0]}` : this.label);

      // Update silently (no onChange) so syncing never triggers another render. setDate ignores disabled
      // days, so a date from the URL only sticks once the release days are enabled (sync runs again then)
      if (iso) {
        this.picker.setDate(iso, false);
      } else {
        this.picker.clear(false);
      }
    }
  }

  customElements.define('arrivals-date-picker', ArrivalsDatePicker);
}
